import { Avatar, Box, Checkbox, List, ListItem, ListItemAvatar, ListItemButton, ListItemIcon, ListItemText, Typography, } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Autosizer, Scrollbar } from 'app/components';
import { TransportGroupRoundedIcon } from 'app/icons';
import { transportGroupsTokenSelector } from 'app/selectors/token';
import { useAsyncAction } from 'app/services';
import { tokenTransportGroupDelete, tokenTransportGroupListDelete, tokenTransportGroupListPush, tokenTransportGroupPush, } from 'app/store/token';
import React, { forwardRef, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { FixedSizeList } from 'react-window';
const TokenTransportGroupList = ({ tokenId, searchValue }) => {
    const transportGroups = useSelector(transportGroupsTokenSelector);
    const checkedLength = transportGroups.filter((item) => !!item.checked).length;
    const isCheckedAll = transportGroups.length > 0 && checkedLength === transportGroups.length;
    const { asyncAction: deleteSubmit, isLoading: isDeleteSubmit } = useAsyncAction(tokenTransportGroupDelete, [tokenId]);
    const { asyncAction: pushSubmit, isLoading: isPushSubmit } = useAsyncAction(tokenTransportGroupPush, [tokenId]);
    const { asyncAction: deleteGroup, isLoading: isDeleteGroup } = useAsyncAction(tokenTransportGroupListDelete, [tokenId]);
    const { asyncAction: addGroup, isLoading: isAddGroup } = useAsyncAction(tokenTransportGroupListPush, [tokenId]);
    const isBusy = isPushSubmit || isDeleteSubmit || isAddGroup || isDeleteGroup;
    const items = useMemo(() => {
        if (searchValue.length) {
            const normalizedSearchValue = searchValue.toLowerCase();
            return transportGroups.filter((item) => item.name.toLowerCase().indexOf(normalizedSearchValue) !== -1);
        }
        return transportGroups;
    }, [transportGroups, searchValue]);
    const handleItemClick = (value) => {
        if (isBusy) {
            return;
        }
        if (value.checked) {
            deleteSubmit(tokenId, value.id);
            return;
        }
        pushSubmit(tokenId, value.id);
    };
    const handleCheckAll = () => {
        if (isBusy || !transportGroups.length) {
            return;
        }
        if (isCheckedAll) {
            deleteGroup(tokenId, transportGroups.map((item) => item.id));
            return;
        }
        addGroup(tokenId, transportGroups.filter((item) => !item.checked).map((item) => item.id));
    };
    return (React.createElement(Box, { component: 'div', sx: (theme) => ({
            width: '100%',
            height: '100%',
            overflow: 'hidden',
            '.simplebar-offset > div': {
                paddingBottom: '36px',
                '&::-webkit-scrollbar': {
                    width: 0,
                },
                '&::-webkit-scrollbar-track': {
                    background: 'transparent',
                    boxShadow: 'none',
                },
                '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                },
            },
            '.MuiListItem-root': {
                width: '100%',
                userSelect: 'none',
                padding: theme.spacing(0),
                borderRadius: '0',
                '.MuiListItemIcon-root': {
                    justifyContent: 'end',
                    '.MuiCheckbox-root': {
                        padding: theme.spacing(0),
                        marginRight: theme.spacing(0),
                        svg: {
                            fontSize: '18px',
                        },
                    },
                },
                '.MuiListItemAvatar-root': {
                    minWidth: '36px',
                },
                '.MuiAvatar-circular': {
                    width: '24px',
                    height: '24px',
                    color: theme.palette.primary.main,
                    bgcolor: alpha(theme.palette.primary.main, 0.12),
                    svg: {
                        fontSize: '16px',
                        path: {
                            fill: theme.palette.primary.main,
                        },
                    },
                },
                '.MuiListItemButton-root': {
                    padding: theme.spacing(1, 2),
                    borderRadius: '0',
                },
                '.MuiListItemText-root': {
                    margin: theme.spacing(0),
                },
            },
        }) },
        React.createElement(Autosizer, null, ({ height, width }) => (React.createElement(Box, { sx: { height, width } },
            React.createElement(Box, { sx: {
                    textAlign: 'end',
                    paddingRight: '14px',
                    cursor: isBusy ? 'default' : 'pointer',
                }, onClick: handleCheckAll },
                React.createElement(Typography, { component: 'span' }, "\u0412\u044B\u0434\u0435\u043B\u0438\u0442\u044C \u0432\u0441\u0451"),
                React.createElement(Checkbox, { size: 'small', edge: 'end', disableRipple: true, checked: isCheckedAll, disabled: isBusy || !transportGroups.length })),
            React.createElement(Scrollbar, null, ({ scrollableNodeRef, contentNodeRef }) => (React.createElement(FixedSizeList, { height: height, itemSize: 36, overscanCount: 10, itemCount: items.length, innerRef: contentNodeRef, outerRef: scrollableNodeRef, innerElementType: tokenTransportGroupListInnerElementType, itemData: {
                    items,
                    onClick: handleItemClick,
                    disabled: isBusy,
                } }, TokenTransportGroupListItemRender))))))));
};
const tokenTransportGroupListInnerElementType = forwardRef((props, ref) => (React.createElement(List, Object.assign({ ref: ref, disablePadding: true }, props))));
tokenTransportGroupListInnerElementType.displayName = 'TokenTransportGroupListInnerElementType';
const TokenTransportGroupListItemRender = ({ data, index, style }) => {
    const { items, onClick, disabled } = data;
    const item = items[index];
    return (React.createElement(ListItem, { style: style, onClick: () => !disabled && onClick(item) },
        React.createElement(ListItemButton, { disabled: disabled },
            React.createElement(ListItemAvatar, null,
                React.createElement(Avatar, null,
                    React.createElement(TransportGroupRoundedIcon, { fontSize: 'small' }))),
            React.createElement(ListItemText, { primary: item === null || item === void 0 ? void 0 : item.name }),
            React.createElement(ListItemIcon, null,
                React.createElement(Checkbox, { size: 'small', edge: 'end', disableRipple: true, disabled: disabled, checked: !!(item === null || item === void 0 ? void 0 : item.checked) })))));
};
export default TokenTransportGroupList;
